import uniq from 'utils/uniq';

const getAliasedProduct = (productId, formula, aliases = {}) => {
  const parts = formula.split('(.*)');
  const productValues = [];

  let name = productId.substr(parts[0].length);
  for (let index = 1; index < parts.length; index += 1) {
    const part = parts[index];
    const value =
      name.indexOf(part) > 0 && part.length > 0 ? name.substr(0, name.indexOf(part)) : name;

    productValues.push(aliases[value] ?? value);

    name = name.substr(value.length + part.length);
  }

  const aliasedId = parts.reduce((acc, part, index) => {
    acc += part;
    if (productValues[index] != null) {
      acc += productValues[index];
    }
    return acc;
  }, '');

  return { productId, aliasedId, productValues };
};

/**
 * Map each categoryLabels array to the same format
 * @param {Array} categoryLabels
 * @param {object} otherProps
 * @returns {Array}
 */
const getCategories = (categoryLabels, otherProps = {}) => {
  return categoryLabels.map((category, index) => {
    // category should be string or object
    let categoryLabel = category;
    let categoryIndex = index;
    let categorySort;
    let categoryKey = category;
    let categoryValues = [];
    if (typeof category === 'object') {
      categoryLabel = category.label;
      categoryIndex = category.index ?? index;
      categorySort = category.sort ?? undefined;
      categoryKey = category.key ?? category.label;
      categoryValues = category.values ?? [];
    }

    const unavailable = (otherProps?.unavailable ?? []).reduce((acc, item) => {
      const [itemName, unavailableType] = Object.entries(item)[0];
      const [cat, lab] = itemName.split('.');

      if (cat === categoryKey) {
        acc.push({ label: lab, type: unavailableType });
      }

      return acc;
    }, []);

    let infoOnly = false;
    if (otherProps.infoOnly) {
      infoOnly = otherProps.infoOnly.indexOf(categoryKey) >= 0;
    }
    const availableOnly = otherProps.availableOnly ?? false;

    return {
      key: categoryKey,
      index: categoryIndex,
      label: categoryLabel,
      sort: categorySort,
      unavailable,
      values: categoryValues,
      infoOnly,
      availableOnly,
    };
  });
};

/**
 * Assign categoriesOrder object
 * @param {Array} categoryLabels
 * @returns {object}
 */
const getCategoriesOrder = categoryLabels => {
  return getCategories(categoryLabels).reduce((acc, category) => {
    acc[category.key] = category.index;

    return acc;
  }, {});
};

const configReducer = (
  [categoryLabels = [], aliases = {}, otherProps = {}],
  productOptions = [],
  config = null,
) => {
  const categories = getCategories(categoryLabels, otherProps);

  if (config) {
    const invertedAliases = {};
    Object.entries(aliases).forEach(([key, value]) => {
      invertedAliases[value] = key;
    });

    const realConfig = categories.reduce((acc, category) => {
      const value = config[category.key];
      acc[category.key] = invertedAliases[value] ?? value;
      return acc;
    }, {});

    // return chepeast product
    // TODO in config we have selected type or something, we can filter by formula and get correct price
    const cheapestProduct = productOptions.sort(({ price: a }, { price: b }) => a - b)[0];
    return [cheapestProduct, realConfig];
  }

  const categoriesOrder = getCategoriesOrder(categoryLabels);

  return categories
    .sort(({ key: a }, { key: b }) => categoriesOrder[a] - categoriesOrder[b])
    .map(category => ({
      ...category,
      values: uniq(category.values).map(value => aliases[value] ?? value),
    }));
};

const productIdReducer = (
  [formula = '', categoryLabels = [], aliases = {}, otherProps = {}],
  productOptions = [],
  config = null,
) => {
  const categories = getCategories(categoryLabels, otherProps);

  // When user defines some configuration
  if (config) {
    const invertedAliases = {};
    Object.entries(aliases).forEach(([key, value]) => {
      invertedAliases[value] = key;
    });

    // Find most similiar option or check if this option isPossible
    if (config.firstAvailable || config.isPossible) {
      const values = categories
        .map(category => ({
          priority: categories.length - category.index,
          value: invertedAliases[config[category.key]] ?? config[category.key],
        }))
        .filter(item => item.value != null);

      const availableOptions = productOptions
        .map(product => {
          const { aliasedId, productValues } = getAliasedProduct(product.id, formula, aliases);
          const score = [];
          values.forEach(({ value, priority }) => {
            if (productValues.indexOf(aliases[value] ?? value) >= 0) {
              if (
                value === invertedAliases[config.firstAvailable] ||
                value === config.firstAvailable
              ) {
                score.push(priority * 3);
              } else {
                score.push(priority);
              }
            }
          });
          return { product, score };
        })
        .filter(option => {
          if (config.isPossible) {
            return option.score.length >= Object.keys(config).length - 1;
          }
          return option.score.length > 0;
        })
        .map(option => ({
          ...option,
          score: option.score.reduce((total, value) => total + value, 0),
        }))
        .sort((a, b) => b.score - a.score);

      if (availableOptions.length > 0) {
        const { product: availableOption } = availableOptions[0];

        const availableValues = [];

        const parts = formula.split('(.*)');
        let name = availableOption.id.substr(parts[0].length);
        for (let index = 1; index < parts.length; index += 1) {
          const part = parts[index];
          const value =
            name.indexOf(part) > 0 && part.length > 0 ? name.substr(0, name.indexOf(part)) : name;

          availableValues.push(value);

          name = name.substr(value.length + part.length);
        }

        const availableConfig = {};
        availableValues.forEach((value, index) => {
          availableConfig[categories[index].key] = aliases[value] ?? value;
        });

        return availableConfig;
      }

      return null;
    }

    let id = formula;
    categories.forEach(category => {
      id = id.replace('(.*)', invertedAliases[config[category.key]] ?? config[category.key]);
    });
    // Try to remove last from id, because of it could be empty param
    return productOptions.find(p => p.id === id || p.id === id.slice(0, -1));
  }

  productOptions.forEach(skuProduct => {
    const values = [];

    const parts = formula.split('(.*)');
    let name = skuProduct.id.substr(parts[0].length);
    for (let index = 1; index < parts.length; index += 1) {
      const part = parts[index];
      const value =
        name.indexOf(part) > 0 && part.length > 0 ? name.substr(0, name.indexOf(part)) : name;

      values.push(value);

      name = name.substr(value.length + part.length);
    }

    values.forEach((value, index) => {
      categories[index].values.push(aliases[value] ?? value);
    });
  });

  const categoriesOrder = getCategoriesOrder(categoryLabels);

  return categories
    .sort(({ key: a }, { key: b }) => categoriesOrder[a] - categoriesOrder[b])
    .map(category => ({
      ...category,
      values: uniq(category.values),
    }));
};

const REDUCER = {
  productIdReducer,
  configReducer,
};

const productReducer = ([type, ...args], productOptions, config = null) => {
  return REDUCER[type](args, productOptions, config);
};

export default productReducer;

/**
 * Attribute values of one SKU (e.g. size and finish) parsed from its id by the
 * category's productIdReducer, in the order the specification lists them.
 * Categories without a productIdReducer have no attributes.
 * @param {Array} reducer PRODUCT_SPECIFICATION reducer, e.g. ['productIdReducer', 'artprint-(.*)-(.*)', [...], {...}]
 * @param {string} skuId
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export const getSkuAttributes = (reducer, skuId) => {
  if (reducer?.[0] !== 'productIdReducer') {
    return [];
  }

  const [, , categoryLabels = []] = reducer;
  const order = getCategories(categoryLabels).map(category => category.key);

  return productReducer(reducer, [{ id: skuId }])
    .filter(category => category.values[0])
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
    .map(category => ({ key: category.key, label: category.label, value: category.values[0] }));
};
